import { useState } from "react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { TID } from "../constants/testIds";
import { Mail, Phone, Instagram, Facebook, MapPin, MessageCircle } from "lucide-react";
import { toast, Toaster } from "sonner";
import Seo from "../components/Seo";

const Kontakt = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Fehler beim Senden");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Kontakt – KK Academy Wil SG"
        description="Kontaktiere die KK Academy in 9500 Wil SG. Telefon, E-Mail und Kontaktformular für Musik- und Tanzunterricht, Workshops und Events."
        path="/kontakt"
      />
      <Toaster position="top-center" theme="light" />
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{t("nav.contact")}</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] max-w-3xl mb-4">{t("contact.title")}</h1>
        <p className="text-muted-foreground mb-12">{t("contact.sub")}</p>

        <div className="grid md:grid-cols-2 gap-12">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("contact.name")}</label>
              <input
                required
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid={TID.contactName}
                className="w-full bg-input/30 border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("enroll.email")}</label>
              <input
                required type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid={TID.contactEmail}
                className="w-full bg-input/30 border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("contact.message")}</label>
              <textarea
                required rows={5}
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                data-testid={TID.contactMessage}
                className="w-full bg-input/30 border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <button type="submit" disabled={loading} data-testid={TID.contactSubmit} className="px-8 py-3.5 rounded-full font-medium btn-gold disabled:opacity-50">
              {loading ? "…" : t("contact.send")}
            </button>
          </form>
          <div className="space-y-5">
            <div className="card-academy rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">KK Academy</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-primary" /><a href="mailto:kk@kkacademy.ch">kk@kkacademy.ch</a></div>
                <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-primary" /><a href="tel:+41793966660">+41 79 396 66 60</a></div>
                <div className="flex items-center gap-3"><MessageCircle className="w-4 h-4 text-[#25D366]" /><a href="https://wa.me/41793966660?text=Hallo%20KK%20Academy%2C%20ich%20interessiere%20mich%20f%C3%BCr..." target="_blank" rel="noreferrer" className="hover:text-[#25D366]">WhatsApp · +41 79 396 66 60</a></div>
                <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-primary" />Wil SG · Schweiz</div>
              </div>
              <div className="border-t border-border my-5"></div>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/kkacademy.ch" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><Instagram className="w-5 h-5" /></a>
                <a href="https://www.facebook.com/share/18YVX38RuW/?mibextid=wwXIfr" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><Facebook className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kontakt;
